import { useApi, useRealtimeEvent, useRealtimeStatus, qk } from '@deskworks/client/react';
import { Avatar, Icon, IconButton, Logo, Menu, cx, toast, type IconName } from '@deskworks/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { CallStrip, IncomingCall } from '../meetings/call.js';
import { desktop, setBadge, systemNotify } from './platform.js';
import { unsavedWork, useMe, useSession } from './session.js';
import { useLayout } from './theme.js';
import { UpdateBar } from './updates.js';

interface ModuleDef {
  path: string;
  label: string;
  icon: IconName;
}

export const MODULES: ModuleDef[] = [
  { path: '/mail', label: 'Mail', icon: 'mail' },
  { path: '/calendar', label: 'Calendar', icon: 'calendar' },
  { path: '/chat', label: 'Chat', icon: 'chat' },
  { path: '/meetings', label: 'Meetings', icon: 'meetings' },
  { path: '/remote', label: 'Remote', icon: 'remote' },
  { path: '/people', label: 'People', icon: 'people' },
  { path: '/files', label: 'Files', icon: 'files' },
];

function useUnreadActivity() {
  const api = useApi();
  const q = useQuery({ queryKey: [...qk.activity, 'count'], queryFn: () => api.activityCount(), refetchInterval: 120_000 });
  return q.data?.unread ?? 0;
}

function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState(() => new URLSearchParams(location.search).get('q') ?? '');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !(e.target as HTMLElement).closest('input,textarea,[contenteditable]'))) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <form
      className="dw-global-search"
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim()) navigate(`/search?q=${encodeURIComponent(value.trim())}`);
      }}
    >
      <Icon name="search" size={18} />
      <label htmlFor="dw-global-search" className="dw-visually-hidden">
        Search mail, events, chat, people and files
      </label>
      <input
        id="dw-global-search"
        ref={ref}
        type="search"
        placeholder="Search mail, people, and conversations"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoComplete="off"
      />
      {!value ? (
        <kbd className="dw-global-search__kbd" aria-hidden="true">
          {/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl'} K
        </kbd>
      ) : null}
    </form>
  );
}

function Header() {
  const me = useMe();
  const { signOut } = useSession();
  const navigate = useNavigate();
  const unread = useUnreadActivity();
  const layout = useLayout();
  useEffect(() => setBadge(unread), [unread]);
  return (
    <header className="dw-header">
      <NavLink to="/mail" className="dw-header__brand" aria-label="DeskWorks One home">
        <Logo tone="white" />
      </NavLink>
      {layout !== 'narrow' ? <GlobalSearch /> : null}
      <div className="dw-header__actions">
        {layout === 'narrow' ? <IconButton variant="header" icon="search" label="Search" onClick={() => navigate('/search')} /> : null}
        <IconButton variant="header" icon="bell" label={unread ? `Activity, ${unread} unread` : 'Activity'} badge={unread} onClick={() => navigate('/activity')} />
        <IconButton variant="header" icon="settings" label="Settings" onClick={() => navigate('/settings')} />
        <Menu
          trigger={(p) => (
            <button type="button" className="dw-header__me" aria-label={`${me.user.initials}, account menu for ${me.user.displayName}`} {...p}>
              <Avatar initials={me.user.initials} size={34} />
            </button>
          )}
          items={[
            { label: me.user.displayName, icon: 'people', onSelect: () => navigate(`/people/${me.user.id}`) },
            { label: 'Settings', icon: 'settings', onSelect: () => navigate('/settings') },
            {
              label: 'Sign out',
              icon: 'logout',
              onSelect: async () => {
                const pending = unsavedWork();
                if (pending.length && !window.confirm(`You have unsaved work (${pending.join(', ')}). Sign out anyway?`)) return;
                await signOut();
                navigate('/signin');
              },
            },
          ]}
        />
      </div>
    </header>
  );
}

function Rail() {
  const ref = useRef<HTMLElement>(null);
  const location = useLocation();
  const [ink, setInk] = useState<{ y: number; h: number } | null>(null);
  // A single highlight that glides to the active module.
  useLayoutEffect(() => {
    const active = ref.current?.querySelector<HTMLElement>('.dw-rail__item.is-active');
    setInk(active ? { y: active.offsetTop, h: active.offsetHeight } : null);
  }, [location.pathname]);
  const item = ({ isActive }: { isActive: boolean }) => cx('dw-rail__item', isActive && 'is-active');
  return (
    <nav className={cx('dw-rail', ink && 'has-ink')} aria-label="Modules" ref={ref}>
      {ink ? <span className="dw-rail__ink" aria-hidden="true" style={{ height: ink.h, transform: `translateY(${ink.y}px)` }} /> : null}
      {MODULES.map((m) => (
        <NavLink key={m.path} to={m.path} className={item} viewTransition>
          <Icon name={m.icon} size={22} />
          <span>{m.label}</span>
        </NavLink>
      ))}
      <NavLink to="/activity" className={item} viewTransition>
        <Icon name="activity" size={22} />
        <span>Activity</span>
      </NavLink>
      <NavLink to="/settings" className={({ isActive }) => cx('dw-rail__item dw-rail__bottom', isActive && 'is-active')} aria-label="Settings" viewTransition>
        <Icon name="settings" size={22} />
      </NavLink>
    </nav>
  );
}

function MobileTabs() {
  const location = useLocation();
  const moreActive = ['/remote', '/people', '/files', '/activity', '/settings', '/search'].some((p) => location.pathname.startsWith(p));
  const tabs: ModuleDef[] = [...MODULES.slice(0, 4), { path: '/more', label: 'More', icon: 'more' }];
  return (
    <nav className="dw-tabs-bottom" aria-label="Modules">
      {tabs.map((t) => (
        <NavLink
          key={t.path}
          viewTransition
          to={t.path === '/more' ? '/more' : t.path}
          className={({ isActive }) => cx('dw-tabs-bottom__item', (isActive || (t.path === '/more' && moreActive)) && 'is-active')}
        >
          <Icon name={t.icon} size={22} />
          <span>{t.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function StatusBar() {
  const api = useApi();
  const status = useRealtimeStatus();
  const accounts = useQuery({ queryKey: qk.accounts, queryFn: () => api.accounts() });
  const ops = useQuery({ queryKey: qk.operations, queryFn: () => api.operations(), refetchInterval: 30_000 });
  const navigate = useNavigate();
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(navigator.onLine);
    window.addEventListener('online', on);
    window.addEventListener('offline', on);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', on);
    };
  }, []);
  const attention = (accounts.data ?? []).filter((a) => a.problem || a.services.mail === 'auth_failed');
  const syncing = (accounts.data ?? []).some((a) => a.syncProgress.phase !== 'idle');
  const pending = (ops.data ?? []).filter((o) => o.state === 'pending' || o.state === 'submitting');
  const failed = (ops.data ?? []).filter((o) => o.state === 'failed' || o.state === 'unknown');

  let text = 'All accounts up to date';
  let tone: 'ok' | 'warn' | 'busy' = 'ok';
  if (!online) {
    text = 'You are offline. Changes will be kept here until you reconnect.';
    tone = 'warn';
  } else if (status === 'reconnecting') {
    text = 'Reconnecting…';
    tone = 'busy';
  } else if (failed.length) {
    text = `${failed.length} item${failed.length === 1 ? '' : 's'} need${failed.length === 1 ? 's' : ''} attention`;
    tone = 'warn';
  } else if (attention.length) {
    text = `${attention[0]!.email} needs attention`;
    tone = 'warn';
  } else if (pending.length) {
    text = `Sending ${pending.length} item${pending.length === 1 ? '' : 's'}…`;
    tone = 'busy';
  } else if (syncing) {
    text = 'Updating accounts…';
    tone = 'busy';
  } else if ((accounts.data ?? []).length === 0 && accounts.isSuccess) {
    text = 'No mail accounts connected';
  }
  return (
    <footer className={cx('dw-statusbar', `dw-statusbar--${tone}`)}>
      <button
        type="button"
        className="dw-statusbar__text"
        onClick={() => navigate(failed.length ? '/mail/outbox' : attention.length || !accounts.data?.length ? '/settings/accounts' : '/mail')}
      >
        {tone === 'warn' ? <Icon name="warning" size={14} /> : tone === 'busy' ? <Icon name="sync" size={14} /> : <span className="dw-statusbar__dot" aria-hidden="true" />}
        <span role="status">{text}</span>
      </button>
      <span className="dw-statusbar__brand">DeskWorks One</span>
    </footer>
  );
}

/** Desktop/system notifications for new activity while the app is in the background. */
function ActivityNotifier() {
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();
  useRealtimeEvent('activity.new', async () => {
    if (document.visibilityState === 'visible') return;
    const items = await api.activity(true).catch(() => []);
    const latest = items[0];
    if (!latest) return;
    const body = me.settings.notificationPreviews ? (latest.body ?? '') : '';
    systemNotify(latest.title, body, latest.link, navigate);
  });
  useRealtimeEvent('session.revoked', () => toast('This device was signed out.'));
  // Workspace removal: refresh memberships so removed workspaces disappear immediately.
  const { refresh } = useSession();
  useRealtimeEvent('conversation.list', (n) => {
    if (n.reason === 'removed') void refresh();
  });
  // Desktop app: notification clicks and deskworks:// links navigate within the app.
  useEffect(() => desktop?.onNavigate((path) => path.startsWith('/') && navigate(path)), [navigate]);
  return null;
}

export function AppShell({ children }: { children?: ReactNode }) {
  const layout = useLayout();
  const location = useLocation();
  const inMeetingRoom = /^\/meetings\/[0-9a-f-]{36}/.test(location.pathname);
  return (
    <div className={cx('dw-app', `dw-app--${layout}`)}>
      <a href="#dw-main" className="dw-skip">
        Skip to content
      </a>
      <Header />
      <UpdateBar />
      <div className="dw-app__body">
        {layout !== 'narrow' ? <Rail /> : null}
        <main id="dw-main" className="dw-app__main" tabIndex={-1}>
          {children ?? <Outlet />}
        </main>
      </div>
      {!inMeetingRoom ? <CallStrip /> : null}
      {layout === 'narrow' ? <MobileTabs /> : <StatusBar />}
      <IncomingCall />
      <ActivityNotifier />
    </div>
  );
}
